import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

import { API_URL, resolveBackendPath, resolveUploadsPath, getLocalThumbnailUrl, getLocalImageUrl, getLocalCompressedUrl, getDriveThumbnailUrl, toDriveThumbnailUrl } from '../config';

// Prefer local backend thumbnail/image URLs (avoids Google Drive rate limits); fallback to Drive URLs only when no token.
const matchThumbnail = (m) =>
  getLocalThumbnailUrl(m) || toDriveThumbnailUrl(m.thumbnail_url) || toDriveThumbnailUrl(m.image_url) || getDriveThumbnailUrl(m.drive_file_id) || `${API_URL}/photos/${m.photo_token || m.photo_id}/thumbnail`;
const matchImage = (m) =>
  getLocalImageUrl(m) || getLocalThumbnailUrl(m) || toDriveThumbnailUrl(m.image_url) || toDriveThumbnailUrl(m.thumbnail_url) || getDriveThumbnailUrl(m.drive_file_id) || `${API_URL}/photos/${m.photo_token || m.photo_id}`;
const matchCompressed = (m) =>
  getLocalCompressedUrl(m) || getLocalThumbnailUrl(m) || toDriveThumbnailUrl(m.image_url) || toDriveThumbnailUrl(m.thumbnail_url) || getDriveThumbnailUrl(m.drive_file_id) || `${API_URL}/photos/${m.photo_token || m.photo_id}/compressed`;


const VerifyPage = () => {
  const { token } = useParams();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [linkActive, setLinkActive] = useState(true);
  const [linkStatusChecked, setLinkStatusChecked] = useState(false);
  const [eventName, setEventName] = useState('');
  const [userName, setUserName] = useState('');
  const [bannerImage, setBannerImage] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxFromAllPhoto, setLightboxFromAllPhoto] = useState(false);
  const [lightboxZoom, setLightboxZoom] = useState(1);
  const [lightboxPan, setLightboxPan] = useState({ x: 0, y: 0 });
  const [lightboxDragging, setLightboxDragging] = useState(false);
  const lightboxDragRef = useRef({ isDragging: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0 });
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('myPhoto'); // 'myPhoto' | 'allPhoto'
  const [allPhotos, setAllPhotos] = useState([]);
  const [allPhotosLoading, setAllPhotosLoading] = useState(false);
  const [showAllPhotoTab, setShowAllPhotoTab] = useState(true); // from API: show All Photo tab on verify page
  const [selectedPhotoTokens, setSelectedPhotoTokens] = useState([]);
  const allPhotosFetchedForTokenRef = useRef(null); // avoid re-fetching (and re-loading thumbnails) when switching back to All Photo
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null); // hold current camera stream so we can stop it when switching to All Photo tab
  const canvasRef = useRef(null);
  const touchStartX = useRef(null);
  const pinchRef = useRef({ active: false, initialDistance: 0, initialZoom: 1, focalX: 0, focalY: 0, initialPanX: 0, initialPanY: 0 });
  const touchPanRef = useRef({ active: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0, zoom: 1 });
  const historyStatePushed = useRef(false);
  const lightboxHistoryPushed = useRef(false);
  const isHandlingLightboxBack = useRef(false);
  const downloadMenuRef = useRef(null);
  const lightboxOverlayRef = useRef(null);

  // Check link status on mount
  useEffect(() => {
    const checkLinkStatus = async () => {
      if (!token) {
        setError('Verification token is missing from URL');
        setLinkActive(false);
        setLinkStatusChecked(true);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/verify/${token}/status`);
        setLinkActive(response.data.active);
        setEventName(response.data.event_name || '');
        setUserName(response.data.user_name || '');
        setShowAllPhotoTab(response.data.show_all_photo_tab !== false);
        setBannerImage(response.data.banner_image || null);
        if (!response.data.active) {
          setError(response.data.message || 'Verification link is deactivated. Please contact administrator.');
        }
      } catch (error) {
        console.error('Error checking link status:', error);
        if (error.response?.status === 404) {
          setError('Invalid verification link. Please check the link and try again.');
          setLinkActive(false);
        } else {
          // If status check fails, assume link is active (don't block user)
          setLinkActive(true);
        }
      } finally {
        setLinkStatusChecked(true);
      }
    };

    checkLinkStatus();
  }, [token]);

  // Fetch all event photos when "All Photo" tab is selected (only once per token; cache to avoid re-fetching thumbnails on tab switch)
  useEffect(() => {
    if (activeTab !== 'allPhoto' || !token || !linkActive) return;
    if (allPhotosFetchedForTokenRef.current === token && allPhotos.length > 0) {
      return; // already have data for this token, don't refetch
    }
    let cancelled = false;
    setAllPhotosLoading(true);
    axios.get(`${API_URL}/verify/${token}/photos`)
      .then((res) => {
        if (!cancelled && res.data && Array.isArray(res.data.photos)) {
          setAllPhotos(res.data.photos);
          allPhotosFetchedForTokenRef.current = token;
        }
      })
      .catch(() => {
        if (!cancelled) setAllPhotos([]);
        allPhotosFetchedForTokenRef.current = null;
      })
      .finally(() => {
        if (!cancelled) setAllPhotosLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, token, linkActive]);

  useEffect(() => {
    const title = eventName ? `${eventName} — Find My Photos` : 'Memories | Verify';
    document.title = title;
    return () => { document.title = 'AI Pixel'; };
  }, [eventName]);

  // When switching to All Photo tab: stop camera (streamRef survives unmount of video element)
  useEffect(() => {
    if (activeTab === 'allPhoto') {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject = null;
      }
      setShowCamera(false);
      setCameraReady(false);
    }
  }, [activeTab]);

  // Auto-start camera when on My Photo tab, link is active, and no preview/results
  useEffect(() => {
    if (activeTab === 'myPhoto' && linkStatusChecked && linkActive && !preview && !results && !showCamera && !uploading) {
      const timer = setTimeout(() => {
        startCamera();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [activeTab, linkStatusChecked, linkActive, preview, results, showCamera, uploading]);

  // Cleanup camera stream when component unmounts
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!video || !video.videoWidth || !video.videoHeight) {
      setError('Camera not ready. Please wait a moment and try again.');
      return;
    }
    
    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas (mirror it back to normal)
    context.save();
    context.scale(-1, 1);
    context.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
    context.restore();

    // Convert canvas to blob (full rectangular area, no cropping)
    // Use high quality (0.98) to ensure better face detection
    canvas.toBlob((blob) => {
      if (blob) {
        const capturedFile = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
        setFile(capturedFile);
        setPreview(canvas.toDataURL('image/jpeg', 0.95));
        setShowCamera(false);
        stopCamera();
        
        // Auto-submit for verification immediately after capture
        // Pass the file directly to avoid state timing issues
        setTimeout(() => {
          const mockEvent = { preventDefault: () => {} };
          handleSubmit(mockEvent, capturedFile);
        }, 500);
      } else {
        setError('Failed to capture photo. Please try again.');
      }
    }, 'image/jpeg', 0.98);
  };

  const startCamera = async () => {
    try {
      setError('');
      
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera API not supported in this browser');
      }
      
      // Show camera UI first
      setShowCamera(true);
      
      // Wait a bit for the video element to be rendered
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user', // Use front camera
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      streamRef.current = stream;

      if (videoRef.current) {
        const video = videoRef.current;
        video.srcObject = stream;
        setCameraReady(false);
        
        // Wait for video to be ready and play
        const handleLoadedMetadata = () => {
          video.play().then(() => {
            console.log('Camera started successfully');
            setCameraReady(true);
          }).catch(err => {
            console.error('Error playing video:', err);
            setError('Could not start camera video. Please check permissions.');
            setShowCamera(false);
          });
        };
        
        const handleCanPlay = () => {
          setCameraReady(true);
        };
        
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('canplay', handleCanPlay);
        
        // If metadata is already loaded, play immediately
        if (video.readyState >= 2) {
          video.play().then(() => {
            setCameraReady(true);
          }).catch(err => {
            console.error('Error playing video:', err);
          });
        }
      } else {
        throw new Error('Video element not found');
      }
    } catch (err) {
      console.error('Camera error:', err);
      streamRef.current = null;
      setShowCamera(false);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access and try again.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found. Please connect a camera and try again.');
      } else {
        setError('Could not access camera: ' + (err.message || 'Please check your browser settings'));
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
  };

  const resetVerification = useCallback(() => {
    setFile(null);
    setPreview(null);
    setResults(null);
    setSelectedPhotoTokens([]);
    setError('');
    setShowCamera(false);
    stopCamera();
    historyStatePushed.current = false; // Reset history state flag
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Scroll to top and restart camera
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      startCamera();
    }, 500);
  }, []);

  // Handle browser back button when results are shown
  useEffect(() => {
    if (!results) {
      // Reset history state flag when no results
      historyStatePushed.current = false;
      return;
    }

    // Push a state to history when results are shown (only once, and not when lightbox is open)
    if (!historyStatePushed.current && !lightboxOpen) {
      window.history.pushState({ page: 'results' }, '', window.location.href);
      historyStatePushed.current = true;
    }

    // Handle browser back button
    const handlePopState = (event) => {
      // IMPORTANT: Don't handle if lightbox is open
      if (lightboxOpen) {
        return; // Let lightbox handler deal with it
      }
      
      // Don't handle if lightbox handler is currently processing
      if (isHandlingLightboxBack.current) {
        // Wait a bit and check again - this handles the case where lightbox just closed
        setTimeout(() => {
          if (!isHandlingLightboxBack.current && results && !lightboxOpen) {
            // Ensure state is pushed
            if (!historyStatePushed.current) {
              window.history.pushState({ page: 'results' }, '', window.location.href);
              historyStatePushed.current = true;
            }
            // Show confirmation dialog
            setShowConfirmDialog(true);
          }
        }, 350);
        return;
      }
      
      // If we're on results page and back button is pressed, show confirmation dialog
      if (results && !lightboxOpen) {
        // Ensure state is pushed
        if (!historyStatePushed.current) {
          window.history.pushState({ page: 'results' }, '', window.location.href);
          historyStatePushed.current = true;
        }
        // Push state forward immediately to prevent navigation away
        window.history.pushState({ page: 'results' }, '', window.location.href);
        // Ensure flag is still set
        historyStatePushed.current = true;
        // Show confirmation dialog instead of directly resetting
        setShowConfirmDialog(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [results, resetVerification, lightboxOpen]);

  const handleSubmit = async (e, fileToSubmit = null) => {
    e.preventDefault();
    const fileToUse = fileToSubmit || file;
    
    if (!fileToUse) {
      setError('Please select or capture a selfie');
      return;
    }

    if (!token) {
      setError('Verification token is missing from URL');
      return;
    }

    setUploading(true);
    setError('');
    setResults(null);
    setSelectedPhotoTokens([]);

    const formData = new FormData();
    formData.append('selfie', fileToUse);

    console.log('Submitting verification with token:', token);
    console.log('API URL:', `${API_URL}/verify/${token}`);

    try {
      const response = await axios.post(`${API_URL}/verify/${token}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResults(response.data);
    } catch (err) {
      console.error('Verification error:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setUploading(false);
    }
  };

  // Lightbox functions; fromAllPhoto = true when opened from All Photo tab (hide download there)
  const openLightbox = useCallback((images, index = 0, fromAllPhoto = false) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxFromAllPhoto(fromAllPhoto);
    setLightboxOpen(true);
    // History state will be pushed by the useEffect when lightboxOpen becomes true
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setLightboxImages([]);
    setLightboxIndex(0);
    setLightboxFromAllPhoto(false);
    setLightboxZoom(1);
    setLightboxPan({ x: 0, y: 0 });
    setLightboxDragging(false);
    lightboxHistoryPushed.current = false; // Reset history state flag
    isHandlingLightboxBack.current = false; // Reset flag
  }, []);

  const toggleSelectMatchToken = (token) => {
    if (!token) return;
    setSelectedPhotoTokens((prev) =>
      prev.includes(token) ? prev.filter((t) => t !== token) : [...prev, token]
    );
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
  };

  // Reset zoom and pan when changing image in lightbox
  useEffect(() => {
    if (lightboxOpen) {
      setLightboxZoom(1);
      setLightboxPan({ x: 0, y: 0 });
      setLightboxDragging(false);
    }
  }, [lightboxOpen, lightboxIndex]);

  const LIGHTBOX_ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];
  const lightboxZoomIn = () => {
    setLightboxZoom((z) => {
      const idx = LIGHTBOX_ZOOM_STEPS.findIndex((s) => s >= z);
      const next = idx < 0 ? 3 : LIGHTBOX_ZOOM_STEPS[Math.min(idx + 1, LIGHTBOX_ZOOM_STEPS.length - 1)];
      return next;
    });
  };
  const lightboxZoomOut = () => {
    setLightboxZoom((z) => {
      const idx = LIGHTBOX_ZOOM_STEPS.findIndex((s) => s >= z);
      const prev = idx <= 0 ? 0.5 : LIGHTBOX_ZOOM_STEPS[idx - 1];
      return prev;
    });
  };
  const lightboxZoomReset = () => {
    setLightboxZoom(1);
    setLightboxPan({ x: 0, y: 0 });
  };

  // Pan when zoomed: track drag on the image container
  const onLightboxMouseDown = (e) => {
    if (lightboxZoom <= 1) return;
    e.preventDefault();
    setLightboxDragging(true);
    lightboxDragRef.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      startPanX: lightboxPan.x,
      startPanY: lightboxPan.y,
      zoom: lightboxZoom,
    };
  };
  const onLightboxMouseMove = useCallback((e) => {
    if (!lightboxDragRef.current.isDragging) return;
    // Pan is in pre-scale space (transform: scale(z) translate(px, py)),
    // so divide screen delta by zoom to get the correct local-space offset.
    const z = lightboxDragRef.current.zoom || 1;
    setLightboxPan({
      x: lightboxDragRef.current.startPanX + (e.clientX - lightboxDragRef.current.startX) / z,
      y: lightboxDragRef.current.startPanY + (e.clientY - lightboxDragRef.current.startY) / z,
    });
  }, []);
  const onLightboxMouseUp = useCallback(() => {
    lightboxDragRef.current.isDragging = false;
    setLightboxDragging(false);
  }, []);
  useEffect(() => {
    if (!lightboxOpen) return;
    window.addEventListener('mousemove', onLightboxMouseMove);
    window.addEventListener('mouseup', onLightboxMouseUp);
    return () => {
      window.removeEventListener('mousemove', onLightboxMouseMove);
      window.removeEventListener('mouseup', onLightboxMouseUp);
    };
  }, [lightboxOpen, onLightboxMouseMove, onLightboxMouseUp]);

  // Preload next and previous compressed images for faster navigation
  useEffect(() => {
    if (!lightboxOpen || lightboxImages.length === 0) return;

    const preloadImage = (url) => {
      if (!url) return;
      const img = new Image();
      img.src = url;
    };

    // Preload current compressed image
    const currentImage = lightboxImages[lightboxIndex];
    if (currentImage?.compressed) {
      preloadImage(currentImage.compressed);
    } else if (currentImage?.thumbnail) {
      preloadImage(currentImage.thumbnail);
    }

    // Preload next compressed image
    const nextIndex = (lightboxIndex + 1) % lightboxImages.length;
    const nextImage = lightboxImages[nextIndex];
    if (nextImage?.compressed) {
      preloadImage(nextImage.compressed);
    } else if (nextImage?.thumbnail) {
      preloadImage(nextImage.thumbnail);
    }

    // Preload previous compressed image
    const prevIndex = (lightboxIndex - 1 + lightboxImages.length) % lightboxImages.length;
    const prevImage = lightboxImages[prevIndex];
    if (prevImage?.compressed) {
      preloadImage(prevImage.compressed);
    } else if (prevImage?.thumbnail) {
      preloadImage(prevImage.thumbnail);
    }
  }, [lightboxOpen, lightboxImages, lightboxIndex]);


  const downloadImage = async () => {
    const currentImage = lightboxImages[lightboxIndex];
    if (!currentImage) return;

    // Log download_image for analytics (only when from My Photo and we have photo_id)
    if (token && !lightboxFromAllPhoto && (currentImage.photo_id != null || currentImage.photo_token != null)) {
      const photoId = currentImage.photo_id != null ? currentImage.photo_id : null;
      fetch(`${API_URL}/verify/${token}/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'download_image', photo_id: photoId }),
      }).catch(() => {});
    }

    try {
      const imageUrl = currentImage.url || currentImage.thumbnail;
      // Use filename if available, otherwise use alt, otherwise generate a name
      let filename = currentImage.filename || currentImage.alt || `image-${lightboxIndex + 1}.jpg`;
      // Ensure filename has an extension
      if (!filename.includes('.')) {
        filename = `${filename}.jpg`;
      }
      
      // Fetch the image
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading image:', error);
      // Fallback: open in new tab
      window.open(currentImage.url || currentImage.thumbnail, '_blank');
    }
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxImages.length]);

  // Swipe handlers for mobile
  const minSwipeDistance = 50;
  const swipeDetected = useRef(false);

  const getTouchDistance = (t1, t2) => {
    if (!t1 || !t2) return 0;
    return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  };

  const onTouchStart = (e) => {
    swipeDetected.current = false;
    if (e.targetTouches.length === 2) {
      touchStartX.current = null;
      const t1 = e.targetTouches[0];
      const t2 = e.targetTouches[1];
      // Midpoint of the two fingers in screen coords
      const midX = (t1.clientX + t2.clientX) / 2;
      const midY = (t1.clientY + t2.clientY) / 2;
      // Focal point relative to the container center (screen space)
      const el = lightboxOverlayRef.current;
      const rect = el ? el.getBoundingClientRect() : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
      const focalX = midX - (rect.left + rect.width / 2);
      const focalY = midY - (rect.top + rect.height / 2);
      pinchRef.current = {
        active: true,
        initialDistance: getTouchDistance(t1, t2),
        initialZoom: lightboxZoom,
        focalX,
        focalY,
        initialPanX: lightboxPan.x,
        initialPanY: lightboxPan.y,
      };
    } else if (e.targetTouches.length === 1) {
      pinchRef.current.active = false;
      const touch = e.targetTouches[0];
      // If zoomed in, single-finger drag should pan, not swipe between photos
      if (lightboxZoom > 1) {
        touchPanRef.current = {
          active: true,
          startX: touch.clientX,
          startY: touch.clientY,
          startPanX: lightboxPan.x,
          startPanY: lightboxPan.y,
          zoom: lightboxZoom,
        };
        touchStartX.current = null;
      } else {
        touchPanRef.current.active = false;
        touchStartX.current = touch.clientX;
      }
    }
  };

  const onTouchMove = (e) => {
    if (e.targetTouches.length === 2 && pinchRef.current.active && pinchRef.current.initialDistance > 0) {
      e.preventDefault();
      const currentDistance = getTouchDistance(e.targetTouches[0], e.targetTouches[1]);
      const scale = currentDistance / pinchRef.current.initialDistance;
      const z0 = pinchRef.current.initialZoom;
      const newZoom = Math.min(3, Math.max(0.5, z0 * scale));
      // Adjust pan so the pinch focal point stays fixed on screen.
      // Transform is scale(z) translate(px, py), so screen_pos = z * (elem_pos + pan).
      // For focal point f (screen coords relative to center):
      //   pan_new = pan0 + f * (1/newZoom - 1/z0)
      const { focalX, focalY, initialPanX, initialPanY } = pinchRef.current;
      const panAdjust = 1 / newZoom - 1 / z0;
      setLightboxZoom(newZoom);
      setLightboxPan({
        x: initialPanX + focalX * panAdjust,
        y: initialPanY + focalY * panAdjust,
      });
      return;
    }
    // Single-finger pan when zoomed
    if (e.targetTouches.length === 1 && touchPanRef.current.active) {
      e.preventDefault();
      const touch = e.targetTouches[0];
      const z = touchPanRef.current.zoom || 1;
      setLightboxPan({
        x: touchPanRef.current.startPanX + (touch.clientX - touchPanRef.current.startX) / z,
        y: touchPanRef.current.startPanY + (touch.clientY - touchPanRef.current.startY) / z,
      });
      return;
    }
    if (touchStartX.current !== null) {
      e.preventDefault();
    }
  };

  const onTouchEnd = (e) => {
    if (e.targetTouches.length < 2) {
      pinchRef.current.active = false;
    }
    if (touchPanRef.current.active) {
      touchPanRef.current.active = false;
      return;
    }
    if (e.targetTouches.length > 1) return;
    if (e.targetTouches.length === 1) {
      touchStartX.current = null;
      return;
    }

    if (!touchStartX.current) return;

    const touchEndX = e.changedTouches[0].clientX;
    const distance = touchStartX.current - touchEndX;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && lightboxImages.length > 1) {
      swipeDetected.current = true;
      nextImage();
    } else if (isRightSwipe && lightboxImages.length > 1) {
      swipeDetected.current = true;
      prevImage();
    }

    touchStartX.current = null;
  };

  // Non-passive touchmove so we can preventDefault on pinch (required for iOS)
  useEffect(() => {
    const el = lightboxOverlayRef.current;
    if (!el || !lightboxOpen) return;
    const onMove = (e) => {
      if (e.touches.length === 2) e.preventDefault();
    };
    el.addEventListener('touchmove', onMove, { passive: false });
    return () => el.removeEventListener('touchmove', onMove);
  }, [lightboxOpen]);

  // Handle click on lightbox background - prevent close if swipe was detected
  const handleLightboxClick = (e) => {
    // Only close if clicking the background (not buttons or images) and no swipe was detected
    if (e.target === e.currentTarget && !swipeDetected.current) {
      closeLightbox();
    }
    swipeDetected.current = false;
  };

  // Handle browser back button when lightbox is open
  useEffect(() => {
    if (!lightboxOpen) {
      // Reset history state flag when lightbox is closed
      lightboxHistoryPushed.current = false;
      // When lightbox closes, ensure results state is properly set up
      if (results && !historyStatePushed.current) {
        // Ensure results state is pushed if it wasn't already
        window.history.pushState({ page: 'results' }, '', window.location.href);
        historyStatePushed.current = true;
      }
      return;
    }

    // Push a state to history when lightbox opens (only once)
    if (!lightboxHistoryPushed.current) {
      window.history.pushState({ page: 'lightbox' }, '', window.location.href);
      lightboxHistoryPushed.current = true;
    }

    // Handle browser back button
    const handlePopState = (event) => {
      // If lightbox is open and back button is pressed, close lightbox instead of navigating away
      if (lightboxOpen && lightboxHistoryPushed.current) {
        // Set flag to prevent results handler from executing during this operation
        isHandlingLightboxBack.current = true;
        
        // Close lightbox first
        closeLightbox();
        
        // If we have results, ensure we stay on results page by pushing state back
        // This prevents going further back to capture page
        // Also ensure historyStatePushed flag is set so confirmation dialog works
        if (results) {
          // Use double requestAnimationFrame to ensure DOM updates are complete
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              window.history.pushState({ page: 'results' }, '', window.location.href);
              // Ensure the flag is set so the next back button press shows confirmation
              historyStatePushed.current = true;
              
              // Reset flag after handlers are set up
              setTimeout(() => {
                isHandlingLightboxBack.current = false;
              }, 300);
            });
          });
        } else {
          // Reset flag if no results
          setTimeout(() => {
            isHandlingLightboxBack.current = false;
          }, 100);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [lightboxOpen, closeLightbox, results]);

  // Auto-hide notification after 4 seconds
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
        setNotificationMessage('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  // Handle Escape key for confirmation dialog
  useEffect(() => {
    if (!showConfirmDialog) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowConfirmDialog(false);
        // Push state back to stay on results page
        if (results && historyStatePushed.current) {
          window.history.pushState({ page: 'results' }, '', window.location.href);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showConfirmDialog, results]);

  // Handle click outside download menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }
    };

    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showDownloadMenu]);

  // Show loading state while checking link status
  if (!linkStatusChecked) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Checking link status…</p>
        </div>
      </div>
    );
  }

  // Show deactivated message if link is not active
  if (!linkActive) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-8 px-4">
        <div className="max-w-md mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link Deactivated</h1>
          <p className="text-sm text-slate-600 mb-5">
            {error || 'This verification link has been deactivated. Please contact the event organiser for assistance.'}
          </p>
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
            <p className="text-xs text-slate-500">
              If you believe this is an error, contact the organiser with your verification link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-200/80 sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center mb-1">
              <div className="p-1 rounded-xl" style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>
            <h1 className="text-base font-bold text-slate-900 text-center">Find My Photos</h1>
            {eventName && (
              <p className="text-xs font-semibold text-violet-600 mt-0.5 text-center">{eventName}</p>
            )}
            {!eventName && (
              <p className="text-xs text-slate-500 text-center mt-0.5">Take a selfie to find your event photos</p>
            )}
            {userName && (
              <p className="text-[11px] text-slate-400 mt-0.5 text-center">by {userName}</p>
            )}
          </div>
        </div>
      </div>

      {/* Banner */}
      {bannerImage && (
        <div className="max-w-2xl mx-auto">
          <img
            src={bannerImage}
            alt={eventName || 'Event banner'}
            className="w-full object-cover"
            style={{ maxHeight: '220px' }}
          />
        </div>
      )}

      {/* Notification Message Box */}
      {showNotification && notificationMessage && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-sm w-full px-4 animate-slide-down">
          <div className="bg-slate-900 text-white rounded-2xl shadow-2xl px-4 py-3 flex items-start gap-3 border border-white/10">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="flex-1 text-sm font-medium leading-relaxed">{notificationMessage}</p>
            <button
              onClick={() => { setShowNotification(false); setNotificationMessage(''); }}
              className="flex-shrink-0 text-white/60 hover:text-white transition-colors"
              aria-label="Close notification"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-slate-900 text-center mb-2">Clear Results?</h3>
            <p className="text-sm text-slate-500 text-center mb-5 leading-relaxed">
              Your verification results will be cleared and you&apos;ll return to the capture screen.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  if (results && historyStatePushed.current) {
                    window.history.pushState({ page: 'results' }, '', window.location.href);
                  }
                }}
                className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowConfirmDialog(false); resetVerification(); }}
                className="flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-md active:scale-95"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
              >
                Yes, Clear
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-3 py-3 overflow-x-hidden">
        {/* Tabs: My Photo | All Photo (only when event allows All Photo tab) */}
        {linkStatusChecked && linkActive && showAllPhotoTab && (
          <div className="mb-3 flex rounded-xl bg-slate-200/70 p-1">
            <button
              type="button"
              onClick={() => setActiveTab('myPhoto')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-all ${
                activeTab === 'myPhoto'
                  ? 'bg-white text-violet-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              My Photos
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('allPhoto')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-all ${
                activeTab === 'allPhoto'
                  ? 'bg-white text-violet-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              All Photos
            </button>
          </div>
        )}

        <div className="space-y-2 sm:space-y-3 lg:space-y-4">
          {/* My Photo tab: Photo Capture Section - Hide after verification starts or results are shown */}
          {activeTab === 'myPhoto' && (!uploading && !results) && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
              <div className="w-7 h-7 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h2 className="text-sm font-bold text-white">Take a Selfie</h2>
            </div>
            
            <div className="p-2 sm:p-3 lg:p-4">
              {/* Camera View */}
              {showCamera && !preview && (
                <div className="mb-2 sm:mb-3 lg:mb-4 flex flex-col items-center justify-center gap-4 sm:gap-5 lg:gap-6">
                  {/* Professional Camera Container */}
                  <div className="relative w-full max-w-2xl mx-auto">
                    {/* Outer frame with elegant shadow */}
                    <div className="relative bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl border border-gray-200/60 p-3 sm:p-4 lg:p-5">
                      {/* Inner camera viewport */}
                      <div className="relative w-full aspect-[4/3] rounded-xl sm:rounded-2xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 shadow-inner">
                        <video 
                          ref={videoRef} 
                          autoPlay 
                          playsInline
                          muted
                          className="w-full h-full object-cover"
                          style={{ transform: 'scaleX(-1)', display: cameraReady ? 'block' : 'none' }}
                        ></video>
                        {!cameraReady && (
                          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                            <div className="text-center">
                              <div className="animate-spin rounded-full h-12 w-12 sm:h-14 sm:w-14 border-4 border-gray-300 border-t-blue-500 mx-auto mb-3"></div>
                              <p className="text-gray-600 text-sm font-medium">Starting camera...</p>
                            </div>
                          </div>
                        )}
                        {/* Subtle corner indicators */}
                        {cameraReady && (
                          <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-blue-400/40 rounded-tl-lg"></div>
                            <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-blue-400/40 rounded-tr-lg"></div>
                            <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-blue-400/40 rounded-bl-lg"></div>
                            <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-blue-400/40 rounded-br-lg"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Capture Button - Centered below camera */}
                  <div className="flex flex-col items-center">
                    {/* Professional Capture Button */}
                    <button
                      type="button"
                      onClick={capturePhoto}
                      disabled={!cameraReady}
                      className="relative w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-full bg-white border-4 border-gray-200 shadow-2xl hover:shadow-3xl disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 active:scale-95 flex items-center justify-center group"
                    >
                      {/* Outer ring */}
                      <div className="absolute inset-0 rounded-full border-2 border-gray-300/50"></div>
                      {/* Inner circle */}
                      <div className="w-14 h-14 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 group-hover:from-blue-600 group-hover:via-indigo-600 group-hover:to-purple-700 transition-all shadow-inner"></div>
                      {/* Pulse animation when ready */}
                      {cameraReady && (
                        <div className="absolute inset-0 rounded-full bg-blue-400 opacity-50 animate-ping"></div>
                      )}
                      {/* Icon */}
                      <svg className="absolute w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                    {/* Capture Text */}
                    <span className="mt-3 text-sm font-semibold text-gray-700">Tap to Capture</span>
                    {cameraReady && (
                      <p className="mt-1 text-xs text-gray-500 text-center max-w-xs">
                        Position yourself in the frame and tap the button to capture
                      </p>
                    )}
                  </div>
                  
                  <canvas ref={canvasRef} className="hidden"></canvas>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Preview */}
                {preview && !showCamera && !uploading && (
                  <div className="mb-6">
                    <div className="relative rounded-2xl overflow-hidden shadow-lg border border-gray-200/60 bg-gradient-to-br from-white to-gray-50 group">
                      <img 
                        src={preview} 
                        alt="Preview" 
                        className="w-full h-auto max-h-[300px] sm:max-h-[450px] object-contain bg-white cursor-pointer transition-transform group-hover:scale-[1.01]" 
                        onClick={() => openLightbox([{ url: preview, alt: 'Preview', filename: file?.name || 'preview.jpg' }], 0)}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setFile(null);
                          setPreview(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                          // Restart camera after removing preview
                          setTimeout(() => {
                            startCamera();
                          }, 100);
                        }}
                        className="absolute top-3 right-3 bg-red-500 text-white p-2.5 rounded-full hover:bg-red-600 transition-all shadow-lg hover:shadow-xl transform hover:scale-110 active:scale-95"
                        aria-label="Remove photo"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="mb-5 p-4 bg-red-50/80 border-l-4 border-red-500 rounded-r-xl backdrop-blur-sm">
                    <div className="flex items-start">
                      <div className="p-1 bg-red-100 rounded-full mr-3 mt-0.5">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-red-800 flex-1">{error}</p>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
          )}

          {/* Results Section (My Photo tab only) - Only show when there are results or uploading */}
          {activeTab === 'myPhoto' && (results || uploading) && (
          <div className="bg-white rounded-2xl shadow-sm overflow-visible border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h2 className="text-sm font-bold text-white">Your Matches</h2>
              </div>
              {results && results.matches && (
                <span className="text-xs text-violet-300 font-medium">
                  {results.matches.length} match{results.matches.length !== 1 ? 'es' : ''} found
                </span>
              )}
            </div>
            
            <div className="p-2 sm:p-3 lg:p-4">

              {/* Loading State */}
              {uploading && (
                <div className="text-center py-8">
                  <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto mb-3">
                    <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
                  </div>
                  <p className="text-slate-800 font-semibold text-sm mb-0.5">Verifying your face…</p>
                  <p className="text-xs text-slate-400">This may take a few moments</p>
                </div>
              )}

              {/* Results */}
                  {results && (
                <div>
                  {/* Action buttons: left = verify + selection hints, right = Download */}
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-2 w-full">
                    <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
                      <button
                        onClick={resetVerification}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white transition-all shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap shrink-0"
                        style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Verify Again
                      </button>

                      {results.matches && results.matches.length > 0 && (
                        <>
                          <div className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                              checked={
                                results.matches.every(
                                  (m) => (m.photo_token || m.photo_id) && selectedPhotoTokens.includes(m.photo_token || m.photo_id)
                                ) && results.matches.length > 0
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const allTokens = results.matches
                                    .map((m) => m.photo_token || m.photo_id)
                                    .filter(Boolean);
                                  setSelectedPhotoTokens(allTokens);
                                } else {
                                  setSelectedPhotoTokens([]);
                                }
                              }}
                            />
                            <span className="text-[10px] text-slate-600">
                              Select all ({results.matches.length})
                            </span>
                          </div>
                          <span className="text-[10px] text-slate-500">
                            Select photos below, then download selected.
                          </span>
                        </>
                      )}
                    </div>

                    {/* Matches: Download — pinned to the right */}
                    {results.matches && results.matches.length > 0 && (
                        <div className="relative shrink-0 ml-auto" ref={downloadMenuRef}>
                            {/* Download Button with Dropdown */}
                            <button
                              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-xl hover:bg-emerald-700 transition-all shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap"
                            >
                              <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              <span className="text-xs">Download</span>
                              <svg className={`w-3 h-3 sm:w-3.5 sm:h-3.5 transition-transform duration-200 ${showDownloadMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>

                            {/* Dropdown Menu */}
                            {showDownloadMenu && (
                              <div className="absolute right-0 mt-1.5 w-[min(13.5rem,calc(100vw-1.5rem))] bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50 animate-slide-down">
                                <div className="py-0.5">
                                  {/* Download Selected (Original) */}
                                  <button
                                    onClick={async () => {
                                      setShowDownloadMenu(false);
                                      try {
                                        const selectedSet = new Set(selectedPhotoTokens.filter(Boolean));
                                        const matchesToDownload = results.matches.filter((m) =>
                                          selectedSet.has(m.photo_token || m.photo_id)
                                        );

                                        if (matchesToDownload.length === 0) {
                                          alert('Please select at least one photo to download.');
                                          return;
                                        }

                                        for (let i = 0; i < matchesToDownload.length; i++) {
                                          const match = matchesToDownload[i];
                                          try {
                                            fetch(`${API_URL}/verify/${token}/log`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'download_image', photo_id: match.photo_id }) }).catch(() => {});
                                            const photoUrl = match.photo_token || match.photo_id;
                                            const response = await fetch(`${API_URL}/photos/${photoUrl}`);
                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = match.filename;
                                            document.body.appendChild(a);
                                            a.click();
                                            window.URL.revokeObjectURL(url);
                                            document.body.removeChild(a);
                                            if (i < matchesToDownload.length - 1) {
                                              await new Promise(resolve => setTimeout(resolve, 300));
                                            }
                                          } catch (error) {
                                            console.error(`Error downloading ${match.filename}:`, error);
                                          }
                                        }
                                      } catch (error) {
                                        console.error('Error downloading selected photos:', error);
                                        alert('Some photos failed to download. Please try downloading individually.');
                                      }
                                    }}
                                    className="w-full px-2 py-1.5 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 group"
                                  >
                                    <div className="p-1 rounded-md bg-emerald-100 group-hover:bg-emerald-200 transition-colors relative shrink-0">
                                      <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      <svg className="w-2.5 h-2.5 text-green-600 absolute -bottom-0.5 -right-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs font-semibold text-slate-800 leading-tight">Selected · Original</div>
                                      <div className="text-[10px] text-slate-500 leading-tight mt-0.5">
                                        {selectedPhotoTokens.length > 0 ? `${selectedPhotoTokens.length} photos` : 'Select photos first'}
                                      </div>
                                    </div>
                                  </button>

                                  {/* Download Selected (Compressed) */}
                                  <button
                                    onClick={async () => {
                                      setShowDownloadMenu(false);
                                      try {
                                        const selectedSet = new Set(selectedPhotoTokens.filter(Boolean));
                                        const matchesToDownload = results.matches.filter((m) =>
                                          selectedSet.has(m.photo_token || m.photo_id)
                                        );

                                        if (matchesToDownload.length === 0) {
                                          alert('Please select at least one photo to download.');
                                          return;
                                        }

                                        for (let i = 0; i < matchesToDownload.length; i++) {
                                          const match = matchesToDownload[i];
                                          try {
                                            fetch(`${API_URL}/verify/${token}/log`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'download_image', photo_id: match.photo_id }) }).catch(() => {});
                                            const photoUrl = match.photo_token || match.photo_id;
                                            const response = await fetch(`${API_URL}/photos/${photoUrl}/compressed`);
                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            const filename = match.filename.replace(/\.[^/.]+$/, '') + '.jpg';
                                            a.href = url;
                                            a.download = filename;
                                            document.body.appendChild(a);
                                            a.click();
                                            window.URL.revokeObjectURL(url);
                                            document.body.removeChild(a);
                                            if (i < matchesToDownload.length - 1) {
                                              await new Promise(resolve => setTimeout(resolve, 300));
                                            }
                                          } catch (error) {
                                            console.error(`Error downloading compressed ${match.filename}:`, error);
                                          }
                                        }
                                      } catch (error) {
                                        console.error('Error downloading selected compressed photos:', error);
                                        alert('Some compressed photos failed to download. Please try downloading individually.');
                                      }
                                    }}
                                    className="w-full px-2 py-1.5 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 group"
                                  >
                                    <div className="p-1 rounded-md bg-blue-100 group-hover:bg-blue-200 transition-colors relative shrink-0">
                                      <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      <svg className="w-2.5 h-2.5 text-blue-600 absolute -bottom-0.5 -right-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs font-semibold text-slate-800 leading-tight">Selected · Compressed</div>
                                      <div className="text-[10px] text-slate-500 leading-tight mt-0.5">
                                        {selectedPhotoTokens.length > 0 ? `${selectedPhotoTokens.length} · smaller files` : 'Select photos first'}
                                      </div>
                                    </div>
                                  </button>

                                  {/* Divider */}
                                  <div className="border-t border-slate-100 my-0.5"></div>

                                  {/* Download ZIP (Original) */}
                                  <button
                                    onClick={async () => {
                                      setShowDownloadMenu(false);
                                      try {
                                        const selectedSet = new Set(selectedPhotoTokens.filter(Boolean));
                                        const photoTokens = results.matches
                                          .filter((m) => selectedSet.has(m.photo_token || m.photo_id))
                                          .map((match) => match.photo_token || match.photo_id)
                                          .filter(Boolean);

                                        if (photoTokens.length === 0) {
                                          alert('Please select at least one photo to download.');
                                          return;
                                        }

                                        const response = await fetch(`${API_URL}/verify/download-zip/${token}`, {
                                          method: 'POST',
                                          headers: {
                                            'Content-Type': 'application/json',
                                          },
                                          body: JSON.stringify({ photo_tokens: photoTokens }),
                                        });

                                        const contentType = response.headers.get('content-type') || '';
                                        
                                        if (!response.ok) {
                                          let errorMessage = 'Failed to create ZIP file';
                                          if (contentType.includes('application/json')) {
                                            try {
                                              const errorData = await response.json();
                                              errorMessage = errorData.error || errorMessage;
                                            } catch (e) {
                                              errorMessage = `Server error: ${response.status} ${response.statusText}`;
                                            }
                                          } else {
                                            errorMessage = `Server error: ${response.status} ${response.statusText}`;
                                          }
                                          throw new Error(errorMessage);
                                        }

                                        if (!contentType.includes('application/zip') && !contentType.includes('application/x-zip-compressed')) {
                                          throw new Error('Server returned unexpected response format');
                                        }

                                        const blob = await response.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `face-verification-matches-${Date.now()}.zip`;
                                        document.body.appendChild(a);
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                        document.body.removeChild(a);
                                      } catch (error) {
                                        console.error('ZIP download error:', error);
                                        alert('Error creating ZIP file: ' + (error.message || 'Unknown error'));
                                      }
                                    }}
                                    className="w-full px-2 py-1.5 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 group"
                                  >
                                    <div className="p-1 rounded-md bg-violet-100 group-hover:bg-violet-200 transition-colors shrink-0">
                                      <svg className="w-3.5 h-3.5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs font-semibold text-slate-800 leading-tight">ZIP · Original</div>
                                      <div className="text-[10px] text-slate-500 leading-tight mt-0.5">
                                        {selectedPhotoTokens.length > 0 ? `${selectedPhotoTokens.length} in one file` : 'Select photos first'}
                                      </div>
                                    </div>
                                  </button>

                                  {/* Download ZIP (Compressed) */}
                                  <button
                                    onClick={async () => {
                                      setShowDownloadMenu(false);
                                      try {
                                        const selectedSet = new Set(selectedPhotoTokens.filter(Boolean));
                                        const photoTokens = results.matches
                                          .filter((m) => selectedSet.has(m.photo_token || m.photo_id))
                                          .map((match) => match.photo_token || match.photo_id)
                                          .filter(Boolean);

                                        if (photoTokens.length === 0) {
                                          alert('Please select at least one photo to download.');
                                          return;
                                        }

                                        const response = await fetch(`${API_URL}/verify/download-zip-compressed/${token}`, {
                                          method: 'POST',
                                          headers: {
                                            'Content-Type': 'application/json',
                                          },
                                          body: JSON.stringify({ photo_tokens: photoTokens }),
                                        });

                                        const contentType = response.headers.get('content-type') || '';
                                        
                                        if (!response.ok) {
                                          let errorMessage = 'Failed to create compressed ZIP file';
                                          if (contentType.includes('application/json')) {
                                            try {
                                              const errorData = await response.json();
                                              errorMessage = errorData.error || errorMessage;
                                            } catch (e) {
                                              errorMessage = `Server error: ${response.status} ${response.statusText}`;
                                            }
                                          } else {
                                            errorMessage = `Server error: ${response.status} ${response.statusText}`;
                                          }
                                          throw new Error(errorMessage);
                                        }

                                        if (!contentType.includes('application/zip') && !contentType.includes('application/x-zip-compressed')) {
                                          throw new Error('Server returned unexpected response format');
                                        }

                                        const blob = await response.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `face-verification-matches-compressed-${Date.now()}.zip`;
                                        document.body.appendChild(a);
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                        document.body.removeChild(a);
                                      } catch (error) {
                                        console.error('Compressed ZIP download error:', error);
                                        alert('Error creating compressed ZIP file: ' + (error.message || 'Unknown error'));
                                      }
                                    }}
                                    className="w-full px-2 py-1.5 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 group"
                                  >
                                    <div className="p-1 rounded-md bg-indigo-100 group-hover:bg-indigo-200 transition-colors shrink-0">
                                      <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs font-semibold text-slate-800 leading-tight">ZIP · Compressed</div>
                                      <div className="text-[10px] text-slate-500 leading-tight mt-0.5">
                                        {selectedPhotoTokens.length > 0 ? `${selectedPhotoTokens.length} · smaller ZIP` : 'Select photos first'}
                                      </div>
                                    </div>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                      )}
                  </div>

                  {/* Matches Grid */}
                  {results.matches && results.matches.length > 0 ? (
                    <div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1.5 sm:gap-2">
                          {results.matches.map((match, idx) => {
                            const tokenForMatch = match.photo_token || match.photo_id;
                            const isSelected = tokenForMatch && selectedPhotoTokens.includes(tokenForMatch);
                            return (
                              <div
                                key={idx}
                                className="relative rounded-xl overflow-hidden bg-slate-100 cursor-pointer aspect-square shadow-sm hover:shadow-md transition-shadow"
                                onClick={() => {
                                  const images = results.matches.map(m => ({
                                    url: matchImage(m),
                                    alt: m.filename,
                                    filename: m.filename,
                                    thumbnail: matchThumbnail(m),
                                    compressed: matchCompressed(m),
                                    photo_id: m.photo_id,
                                    photo_token: m.photo_token,
                                  }));
                                  openLightbox(images, idx);
                                }}
                              >
                                {/* Selection checkbox */}
                                {tokenForMatch && (
                                  <div className="absolute top-1.5 left-1.5 z-10">
                                    <label className="inline-flex items-center justify-center">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                                        checked={!!isSelected}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          toggleSelectMatchToken(tokenForMatch);
                                        }}
                                      />
                                    </label>
                                  </div>
                                )}

                                {/* Photo */}
                                <img
                                  src={matchThumbnail(match)}
                                  alt={match.filename}
                                  className={`w-full h-full object-cover ${isSelected ? 'ring-2 ring-violet-500' : ''}`}
                                  onError={(e) => {
                                    const fallbackPath = matchImage(match);
                                    if (e.target.src !== fallbackPath) {
                                      e.target.src = fallbackPath;
                                    } else {
                                      const altPath = match.filepath
                                        ? resolveBackendPath(match.filepath)
                                        : resolveUploadsPath(match.filename);
                                      if (e.target.src !== altPath) e.target.src = altPath;
                                      else e.target.style.display = 'none';
                                    }
                                  }}
                                />

                              </div>
                            );
                          })}
                        </div>
                    </div>
                  ) : results.matches !== undefined && results.matches.length === 0 ? (
                    <div className="text-center py-12 sm:py-16">
                      <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-100 mb-4">
                        <svg className="w-8 h-8 sm:w-10 sm:h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-gray-800 font-semibold text-lg mb-1">No matches found</p>
                      <p className="text-sm text-gray-600">No photos above {results.threshold_used || 30}% similarity threshold</p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          )}

          {/* All Photo tab: all event photos, view only (no download); only when event allows it */}
          {showAllPhotoTab && activeTab === 'allPhoto' && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-bold text-white">All Photos</h2>
                  <p className="text-[10px] text-violet-300">View only · tap a photo to open</p>
                </div>
              </div>
              {allPhotos.length > 0 && (
                <span className="text-xs text-violet-300 font-medium">{allPhotos.length} photos</span>
              )}
            </div>
            <div className="p-3">
              {allPhotosLoading ? (
                <div className="flex flex-col items-center gap-3 py-10">
                  <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-slate-400">Loading photos…</p>
                </div>
              ) : allPhotos.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-sm text-slate-500">No photos in this event yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1.5 sm:gap-2">
                  {allPhotos.map((photo, idx) => (
                    <div
                      key={photo.photo_id || idx}
                      className="relative rounded-xl overflow-hidden bg-slate-100 cursor-pointer aspect-square shadow-sm hover:shadow-md transition-shadow"
                      onClick={() => {
                        const images = allPhotos.map(p => ({
                          url: matchImage(p),
                          alt: p.filename,
                          filename: p.filename,
                          thumbnail: matchThumbnail(p),
                          compressed: matchCompressed(p),
                        }));
                        openLightbox(images, idx, true);
                      }}
                    >
                      <img
                        src={matchThumbnail(photo)}
                        alt={photo.filename}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          if (e.target.src !== matchImage(photo)) e.target.src = matchImage(photo);
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal - use most of viewport so faces are visible */}
      {lightboxOpen && lightboxImages.length > 0 && (
        <div 
          ref={lightboxOverlayRef}
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col items-center justify-center p-1 sm:p-2"
          onClick={handleLightboxClick}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Top Right Buttons */}
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-2 sm:gap-3 z-10">
            {/* Zoom controls */}
            <div className="flex items-center gap-1 bg-black/70 rounded-full px-1 py-1 backdrop-blur-sm shadow-xl">
              <button
                onClick={(e) => { e.stopPropagation(); lightboxZoomOut(); }}
                className="text-white hover:text-gray-200 rounded-full p-2 hover:bg-white/10 transition-all disabled:opacity-40"
                aria-label="Zoom out"
                title="Zoom out"
                disabled={lightboxZoom <= 0.5}
              >
                <svg className="w-5 h-5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); lightboxZoomReset(); }}
                className="text-white/90 hover:text-white text-xs font-medium min-w-[2.5rem] py-1.5"
                title="Reset zoom (1:1)"
              >
                {Math.round(lightboxZoom * 100)}%
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); lightboxZoomIn(); }}
                className="text-white hover:text-gray-200 rounded-full p-2 hover:bg-white/10 transition-all disabled:opacity-40"
                aria-label="Zoom in"
                title="Zoom in"
                disabled={lightboxZoom >= 3}
              >
                <svg className="w-5 h-5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
            {/* Download Button - only when lightbox opened from My Photo (not All Photo tab) */}
            {!lightboxFromAllPhoto && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                downloadImage();
              }}
              className="text-white hover:text-gray-200 transition-all bg-black/70 hover:bg-black/90 rounded-full p-2.5 sm:p-3 shadow-xl backdrop-blur-sm transform hover:scale-110 active:scale-95"
              aria-label="Download image"
              title="Download image"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>
            )}
            
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="text-white hover:text-gray-200 transition-all bg-black/70 hover:bg-black/90 rounded-full p-2.5 sm:p-3 shadow-xl backdrop-blur-sm transform hover:scale-110 active:scale-95"
              aria-label="Close"
              title="Close"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Previous Button */}
          {lightboxImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-200 transition-all z-10 bg-black/70 hover:bg-black/90 rounded-full p-3 sm:p-4 shadow-xl backdrop-blur-sm transform hover:scale-110 active:scale-95"
              aria-label="Previous image"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Main Image Container - fill viewport, zoomable and pannable */}
          <div 
            className="relative flex-1 flex items-center justify-center min-h-0 w-full max-w-[100vw] mb-1 sm:mb-2 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-center transition-transform duration-200 ease-out"
              style={{
                transform: `scale(${lightboxZoom}) translate(${lightboxPan.x}px, ${lightboxPan.y}px)`,
                cursor: lightboxZoom > 1 ? (lightboxDragging ? 'grabbing' : 'grab') : 'default',
              }}
              onMouseDown={onLightboxMouseDown}
              role="presentation"
            >
              {/* Prefer compressed (faster load), then full url, then thumbnail */}
              {lightboxImages[lightboxIndex]?.compressed ? (
                <img
                  key={`compressed-${lightboxIndex}-${lightboxImages[lightboxIndex].compressed}`}
                  src={lightboxImages[lightboxIndex].compressed}
                  alt={lightboxImages[lightboxIndex]?.alt || 'Image'}
                  className="max-w-[96vw] max-h-[92vh] sm:max-h-[94vh] w-auto h-auto object-contain rounded-lg sm:rounded-xl shadow-2xl select-none touch-none pointer-events-none"
                  draggable="false"
                  onError={(e) => {
                    if (lightboxImages[lightboxIndex]?.url && e.target.src !== lightboxImages[lightboxIndex].url) {
                      e.target.src = lightboxImages[lightboxIndex].url;
                    } else if (lightboxImages[lightboxIndex]?.thumbnail && e.target.src !== lightboxImages[lightboxIndex].thumbnail) {
                      e.target.src = lightboxImages[lightboxIndex].thumbnail;
                    }
                  }}
                />
              ) : lightboxImages[lightboxIndex]?.url ? (
                <img
                  key={`img-${lightboxIndex}-${lightboxImages[lightboxIndex].url}`}
                  src={lightboxImages[lightboxIndex].url}
                  alt={lightboxImages[lightboxIndex]?.alt || 'Image'}
                  className="max-w-[96vw] max-h-[92vh] sm:max-h-[94vh] w-auto h-auto object-contain rounded-lg sm:rounded-xl shadow-2xl select-none touch-none pointer-events-none"
                  draggable="false"
                  onError={(e) => {
                    if (lightboxImages[lightboxIndex]?.compressed && e.target.src !== lightboxImages[lightboxIndex].compressed) {
                      e.target.src = lightboxImages[lightboxIndex].compressed;
                    } else if (lightboxImages[lightboxIndex]?.thumbnail && e.target.src !== lightboxImages[lightboxIndex].thumbnail) {
                      e.target.src = lightboxImages[lightboxIndex].thumbnail;
                    }
                  }}
                />
              ) : lightboxImages[lightboxIndex]?.thumbnail ? (
                <img
                  key={`thumb-${lightboxIndex}-${lightboxImages[lightboxIndex].thumbnail}`}
                  src={lightboxImages[lightboxIndex].thumbnail}
                  alt={lightboxImages[lightboxIndex]?.alt || 'Image'}
                  className="max-w-[96vw] max-h-[92vh] sm:max-h-[94vh] w-auto h-auto object-contain rounded-lg sm:rounded-xl shadow-2xl select-none touch-none pointer-events-none"
                  draggable="false"
                  onError={(e) => {
                    if (lightboxImages[lightboxIndex]?.url && e.target.src !== lightboxImages[lightboxIndex].url) {
                      e.target.src = lightboxImages[lightboxIndex].url;
                    }
                  }}
                />
              ) : (
                <div className="max-w-[96vw] max-h-[92vh] flex items-center justify-center bg-gray-800 rounded-xl p-8">
                  <p className="text-white text-sm">Image not available</p>
                </div>
              )}
            </div>
            
            {/* Image Counter */}
            {lightboxImages.length > 1 && (
              <div className="absolute top-3 sm:top-4 left-1/2 transform -translate-x-1/2 bg-black/80 backdrop-blur-md text-white px-4 py-2 rounded-full text-xs sm:text-sm font-semibold shadow-xl">
                {lightboxIndex + 1} / {lightboxImages.length}
              </div>
            )}
          </div>

          {/* Next Button */}
          {lightboxImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-200 transition-all z-10 bg-black/70 hover:bg-black/90 rounded-full p-3 sm:p-4 shadow-xl backdrop-blur-sm transform hover:scale-110 active:scale-95"
              aria-label="Next image"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Thumbnail Strip */}
          {lightboxImages.length > 1 && (
            <div 
              className="w-full max-w-7xl px-2 sm:px-4 pb-2 sm:pb-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 sm:pb-3 lightbox-scrollbar">
                {lightboxImages.map((image, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxIndex(idx);
                    }}
                    className={`flex-shrink-0 relative rounded-lg overflow-hidden transition-all duration-200 ${
                      idx === lightboxIndex
                        ? 'ring-2 ring-white ring-offset-2 ring-offset-black scale-105'
                        : 'opacity-70 hover:opacity-100 hover:scale-105'
                    }`}
                  >
                    <img
                      src={image.thumbnail || image.compressed || image.url}
                      alt={image.alt || `Thumbnail ${idx + 1}`}
                      className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 object-cover"
                      onError={(e) => {
                        // Fallback to compressed or original if thumbnail fails
                        if (image.compressed && e.target.src !== image.compressed) {
                          e.target.src = image.compressed;
                        } else if (image.url && e.target.src !== image.url) {
                          e.target.src = image.url;
                        }
                      }}
                    />
                    {idx === lightboxIndex && (
                      <div className="absolute inset-0 bg-white/20 pointer-events-none"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VerifyPage;
